import { useController } from 'react-hook-form';

import { useIntl } from '@zenbusiness/application-commons-intl';
import isValidEmail from '@zenbusiness/application-commons-utils/isValidEmail';

import useShouldPromptLogin from '#client/hooks/useShouldPromptLogin';
import { buildFieldName } from '#client/utils/form';

import { FIELD_NAMES, EMAIL_MAX_LENGTH } from '../../constants';

const MESSAGE_IDS = {
  label: 'shopping.component.accountFieldset.emailField.label',
  validation: {
    existingAccount: 'shopping.component.accountFieldset.emailField.validation.existingAccount',
    invalid: 'shopping.component.accountFieldset.emailField.validation.invalid',
    maxLength: 'shopping.component.accountFieldset.validation.maxLength',
    required: 'shopping.component.accountFieldset.validation.required'
  }
};

/**
 * Custom hook for email field that handles form integration.
 *
 * @param {object} options - Options for the hook
 * @param {string} [options.defaultValue] - Default value for the field
 * @param {string} [options.namePrefix] - Prefix for the field name
 * @param {object} [options.validation] - Additional validation rules
 * @returns {object} Field and error properties
 */
function useEmailField({ defaultValue = '', namePrefix, validation }) {
  const { formatMessage } = useIntl();
  const shouldPromptLogin = useShouldPromptLogin();

  const defaultValidation = {
    required: formatMessage(
      { id: MESSAGE_IDS.validation.required },
      { label: formatMessage({ id: MESSAGE_IDS.label }) }
    ),
    maxLength: {
      value: EMAIL_MAX_LENGTH,
      message: formatMessage(
        { id: MESSAGE_IDS.validation.maxLength },
        { maxLength: EMAIL_MAX_LENGTH }
      )
    },
    validate: async (value) => {
      if (!isValidEmail(value)) {
        return formatMessage({ id: MESSAGE_IDS.validation.invalid });
      }

      const shouldLogin = await shouldPromptLogin(value);
      if (shouldLogin) {
        return formatMessage({ id: MESSAGE_IDS.validation.existingAccount });
      }

      return true;
    }
  };

  const fieldName = buildFieldName(FIELD_NAMES.EMAIL, namePrefix);

  const {
    field,
    fieldState: { error }
  } = useController({
    defaultValue,
    name: fieldName,
    rules: { ...defaultValidation, ...validation }
  });

  return {
    error,
    field: {
      ...field,
      value: field.value ?? ''
    }
  };
}

export default useEmailField;
